import React, { useEffect, useState, useRef } from 'react';
import makeStyles from "@material-ui/styles/makeStyles";
import withStyles from "@material-ui/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import GetAppIcon from '@material-ui/icons/GetApp';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import api from "../../common/api";
import CircularProgress from "@material-ui/core/CircularProgress";
import html2canvas from 'html2canvas';
import moment from "moment";
import { useReactToPrint } from 'react-to-print';
import { useGlobalState } from '../../state';
import Divider from '@material-ui/core/Divider';
import TableWrapper from "../../components/Table/TableWrapper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

window.html2canvas = html2canvas;

const useStyles = makeStyles(theme => ({
    root: props => ({
        margin: props.isDesktop ? theme.spacing(4) : theme.spacing(1)
    }),
    topBar: { marginBottom: theme.spacing(2) },
    formControl: { width: '100%' },
    progressContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%'
    },
    progressBar: { margin: theme.spacing(2) },
    boldedText: { fontWeight: 'bold' },
}));

const StyledTableCell = withStyles(theme => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    body: {
        fontSize: '1em',
        backgroundColor: theme.palette.common.white,
        padding: '6px'
    },
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.background.default,
        },
    },
}))(TableRow);

const tableHeader = [["Medicine Name", "Movement Date", "Purchase Date", "Quantity In", "In Rate", "Quantity Out", "Out Rate"]];

const generateExcel = (header, data) => {
    let csvData = '';
    header[0].forEach((col, i) => {
        csvData += i === 0 ? col : ',' + col;
    });
    csvData += '\r\n';
    data.forEach(row => {
        csvData += `${row[0]},${row[1] !== "0" ? moment(row[1]).format("DD-MM-YYYY") : ""},${row[2] !== "0" ? moment(row[2]).format("DD-MM-YYYY") : ""},${row[3]},${row[4]},${row[5]},${row[6]}\r\n`;
    });
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData));
    element.setAttribute('download', 'medicine-storage-report.csv');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

const MedicineGlobalStorageReport = ({ showError }) => {
    const theme = useTheme();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [valid, setValid] = useState(false);
    const [data, setData] = useState([]);
    const [wholedata, setWholeData] = useState({});
    const [materialTypeList, setMaterialTypeList] = useState([]);
    const [materialList, setMaterialList] = useState(null);
    const [materialTypeIdx, setMaterialTypeIdx] = useState("");
    const [medicineIdx, setMedicineIdx] = useState("");

    useEffect(() => {
        if (startDate && endDate && startDate.getTime() < endDate.getTime() && materialTypeIdx !== "" && medicineIdx !== "") {
            setValid(true);
        } else {
            setValid(false);
        }
    }, [startDate, endDate, materialTypeIdx, medicineIdx]);

    const getMaterialTypes = () => {
        const response = api.get('material-type');
        response.then(res => {
            const types = [];
            if (res?.Medicine) {
                types.push({ key: 0, value: res.Medicine[0].materialTypeId, type: res.Medicine[0].type });
            }
            if (res?.Vaccination) {
                types.push({ key: 1, value: res.Vaccination[0].materialTypeId, type: res.Vaccination[0].type });
            }
            setMaterialTypeList(types);
            setMaterialList(res);
        }).catch(err => {
            if (err.message) showError(err.message);
        });
        return response;
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([getMaterialTypes()]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const parseReport = (res) => {
        const { reportsArray } = res;
        setData(reportsArray !== null && reportsArray !== undefined ? reportsArray : []);
        setWholeData(res);
    };

    const generateReport = () => {
        showError('');
        setLoading(true);
        const typeName = materialTypeList[materialTypeIdx]?.type;
        const medicine = materialList?.[typeName]?.[medicineIdx];
        const payload = {
            startDate,
            endDate,
            materialType: typeName,
            materialMasterId: medicine?.materialMasterId,
            materialName: medicine?.materialName,
        };
        api.post('reports/medicine-global-storage-report', payload)
            .then(parseReport)
            .catch(err => {
                if (err.message) showError(err.message);
                else showError('Unknown Error');
            })
            .finally(() => setLoading(false));
    };

    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const classes = useStyles({ isDesktop });
    const componentRef = useRef();
    const generatePDF = useReactToPrint({ content: () => componentRef.current });
    const [{ user }] = useGlobalState();
    const amountObj = {
        style: "currency",
        currency: user.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };

    const currentTypeName = materialTypeList[materialTypeIdx]?.type;
    const currentMedicineList = materialList?.[currentTypeName] || [];

    return (
        <div className={classes.root}>
            <Grid container alignItems='flex-start' className={classes.topBar}>
                <Grid>
                    <Typography display="block" variant="h3" gutterBottom>
                        Medicine Storage Report
                    </Typography>
                </Grid>
            </Grid>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container spacing={3} justify="flex-start" alignItems="center">
                    <Grid item>
                        <KeyboardDatePicker
                            margin="normal" autoOk variant="inline" label="From" format="dd/MM/yyyy"
                            value={startDate} onChange={setStartDate}
                            KeyboardButtonProps={{ 'aria-label': 'change date' }}
                        />
                    </Grid>
                    <Grid item>
                        <KeyboardDatePicker
                            margin="normal" autoOk variant="inline" label="To" format="dd/MM/yyyy"
                            value={endDate} onChange={setEndDate}
                            KeyboardButtonProps={{ 'aria-label': 'change date' }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControl style={{ minWidth: "150px" }}>
                            <InputLabel>Material Type</InputLabel>
                            <Select
                                value={materialTypeIdx}
                                onChange={(e) => { setMaterialTypeIdx(e.target.value); setMedicineIdx(""); }}
                                name="materialTypeIdx"
                            >
                                {materialTypeList.map((mt, idx) => (
                                    <MenuItem key={mt.value} value={idx}>{mt.type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl style={{ minWidth: "150px" }}>
                            <InputLabel>Material Name</InputLabel>
                            <Select
                                value={medicineIdx}
                                onChange={(e) => setMedicineIdx(e.target.value)}
                                name="medicineIdx"
                            >
                                {currentMedicineList.map((m, idx) => (
                                    <MenuItem key={m.materialMasterId} value={idx}>{m.materialName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <Button disabled={!valid} variant="contained" color="primary" onClick={generateReport}>
                            Generate
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button disabled={!valid || data.length < 1} variant="outlined" color="primary"
                            onClick={generatePDF} endIcon={<PictureAsPdfIcon />}>
                            Download PDF
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button disabled={!valid || data.length < 1} variant="outlined" color="primary"
                            onClick={() => generateExcel(tableHeader, data)} endIcon={<GetAppIcon />}>
                            Download Excel
                        </Button>
                    </Grid>
                </Grid>
            </MuiPickersUtilsProvider>

            {loading ? (
                <div className={classes.progressContainer}>
                    <CircularProgress className={classes.progressBar} />
                </div>
            ) : (
                <div id="medicine-storage-report" ref={componentRef} style={{ paddingTop: '35px' }}>
                    <div className="invoice-box" style={{
                        backgroundColor: '#ffffff',
                        padding: isDesktop ? "50px" : "10px",
                        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif'
                    }}>
                        <table cellPadding="0" cellSpacing="0">
                            <tbody>
                                <tr>
                                    <td>
                                        <table style={{ width: '100%' }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ textAlign: "right" }}>
                                                        <table style={{ width: "100%" }}>
                                                            <tbody>
                                                                <tr>
                                                                    <td style={{ textAlign: "right", fontWeight: '600', fontSize: '23px' }} colSpan="2">
                                                                        Medicine Storage Report<br />
                                                                        <span style={{ fontWeight: '400', fontSize: '15px' }}>
                                                                            <b>From : </b>{moment(startDate).format('MMM Do YYYY')}
                                                                        </span><br />
                                                                        <span style={{ fontWeight: '400', fontSize: '15px' }}>
                                                                            <b>To : </b>{moment(endDate).format('MMM Do YYYY')}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan="2"><Divider /><br /></td>
                                                                </tr>
                                                            </tbody>
                                                            <tbody>
                                                                <tr>
                                                                    <td style={{ textAlign: "right" }}>Opening Stock</td>
                                                                    <td style={{ textAlign: "right" }}>{wholedata.openingStock ?? 0}</td>
                                                                </tr>
                                                                <tr style={{ fontWeight: 'bold' }}>
                                                                    <td style={{ textAlign: "right" }}>Closing Stock</td>
                                                                    <td style={{ textAlign: "right" }}>{wholedata.closingStock ?? 0}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <br />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <TableWrapper enableKeyboardNavigation={true} focus={data && data.length > 0}>
                            <Table style={{ tableLayout: "auto", maxWidth: "100%" }} stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell>Medicine Name</StyledTableCell>
                                        <StyledTableCell>Movement Date</StyledTableCell>
                                        <StyledTableCell>Purchase Date</StyledTableCell>
                                        <StyledTableCell>In Quantity</StyledTableCell>
                                        <StyledTableCell>In Rate</StyledTableCell>
                                        <StyledTableCell>Out Quantity</StyledTableCell>
                                        <StyledTableCell>Out Rate</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((row, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>{row[0]}</StyledTableCell>
                                            <StyledTableCell>{row[1] !== "0" ? moment(row[1]).format("DD-MM-YYYY") : ""}</StyledTableCell>
                                            <StyledTableCell>{row[2] !== "0" ? moment(row[2]).format("DD-MM-YYYY") : ""}</StyledTableCell>
                                            <StyledTableCell className={row[0].includes('Net') ? classes.boldedText : undefined}>
                                                {row[3] !== "" ? parseFloat(row[3]) : ""}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row[4] !== "" ? parseFloat(row[4]).toLocaleString(user.locales, amountObj) : ""}
                                            </StyledTableCell>
                                            <StyledTableCell className={row[0].includes('Net') ? classes.boldedText : undefined}>
                                                {row[5] !== "" ? parseFloat(row[5]) : ""}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row[6] !== "" ? parseFloat(row[6]).toLocaleString(user.locales, amountObj) : ""}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableWrapper>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MedicineGlobalStorageReport;
